import React, { useState } from 'react'
import { cardStyle, accent, accentLight, primaryBtn, primaryBtnHover, textPrimary } from './styles'
import { formatYear, isSunday, DAILY_CREDIT, DAY_MULTIPLIER } from './gameLogic'


function WagerScreen({ anchor, category, weeklyBank, onWagerSubmit }) {
  const [wager, setWager] = useState(10)
  const [isHovering, setIsHovering] = useState(false)
  const isSundayGame = isSunday()
  const wagerMax = isSundayGame ? Math.floor(weeklyBank) + 50 : 50

  const handleSliderChange = (e) => {
    const value = Math.max(10, Math.min(wagerMax, parseInt(e.target.value)))
    setWager(value)
  }

  const handleSubmit = () => {
    onWagerSubmit(wager)
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#fafbfc', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px', paddingTop: '0' }}>
      <style>{`
        .wager-slider {
          accent-color: ${accent};
          -webkit-appearance: none;
          appearance: none;
          width: 100%;
          height: 6px;
          border-radius: 3px;
          outline: none;
          cursor: pointer;
        }

        .wager-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: ${accent};
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 8px rgba(234, 88, 12, 0.4);
        }

        .wager-slider::-moz-range-thumb {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: ${accent};
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 8px rgba(234, 88, 12, 0.4);
          -moz-appearance: none;
        }

        .wager-slider::-moz-range-track {
          background: transparent;
          border: none;
        }

        .wager-slider::-webkit-slider-runnable-track {
          background: transparent;
          border: none;
        }
      `}</style>
      <div style={{ maxWidth: '500px', width: '100%' }}>
        {/* Category & Anchor */}
        <div style={{ ...cardStyle }}>
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.07em', color: '#aaa', marginBottom: '8px' }}>Today's Category</div>
            <div style={{ fontSize: '1.3rem', fontWeight: '600', color: textPrimary }}>{category}</div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '20px' }}>
            {/* Anchor Event */}
            <div style={{ padding: '16px', backgroundColor: accentLight, borderRadius: '12px', borderLeft: `4px solid ${accent}` }}>
              <div style={{ fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', color: accent, marginBottom: '4px' }}>Anchor Event</div>
              <div style={{ fontSize: '1.1rem', fontWeight: '600', color: textPrimary, marginBottom: '8px' }}>{anchor.title}</div>
              <div style={{ fontSize: '2.5rem', fontWeight: '900', color: accent }}>{formatYear(anchor.year)}</div>
            </div>

            {/* Overview Guidance */}
            <div style={{ padding: '16px', backgroundColor: accentLight, borderRadius: '12px', borderLeft: `4px solid ${accent}` }}>
              <div style={{ fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', color: accent, marginBottom: '8px' }}>Overview</div>
              <div style={{ fontSize: '0.85rem', lineHeight: '1.5', color: textPrimary }}>
                {isSundayGame ? (
                  <>
                    <p style={{ margin: '0 0 8px 0' }}>You get <strong>$50 daily credit</strong>. Wager between <strong>$10-${wagerMax}</strong> with your full weekly bank on the line.</p>
                    <p style={{ margin: '0' }}>All payouts are doubled—both wins and losses.</p>
                  </>
                ) : (
                  <>
                    <p style={{ margin: '0 0 8px 0' }}>You get <strong>$50 daily credit</strong>. Wager between <strong>$10-$50</strong> that you will correctly place 4 events in the proper timeline.</p>
                    <p style={{ margin: '0' }}>Your daily totals roll into Sunday where you can wager your full weekly bank.</p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Wager Controls */}
        <div style={{ ...cardStyle }}>
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.07em', color: '#aaa', marginBottom: '8px' }}>Your Wager</div>

            {/* Sunday Multiplier Badge */}
            {isSundayGame && (
              <div style={{ marginBottom: '12px', padding: '8px 10px', backgroundColor: '#fff7ed', borderRadius: '6px', border: `1px solid ${accent}`, textAlign: 'center' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: '700', color: accent, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  ⚡ 2x Sunday Multiplier
                </div>
              </div>
            )}

            <div style={{ marginBottom: '12px' }}>
              <input
                type="range"
                className="wager-slider"
                min="10"
                max={wagerMax}
                step="1"
                value={wager}
                onChange={handleSliderChange}
                style={{
                  width: '100%',
                  height: '6px',
                  borderRadius: '3px',
                  background: `linear-gradient(to right, ${accent} 0%, ${accent} ${((wager - 10) / (wagerMax - 10)) * 100}%, #e5e7eb ${((wager - 10) / (wagerMax - 10)) * 100}%, #e5e7eb 100%)`,
                  outline: 'none',
                  WebkitAppearance: 'none',
                  appearance: 'none',
                }}
              />
            </div>

            {/* Tick marks positioned to align with slider */}
            {(() => {
              const range = wagerMax - 10
              if (range <= 0) return null

              const tickValues = []
              const percentages = [0.25, 0.5, 0.75, 1.0]

              for (let i = 0; i < percentages.length; i++) {
                tickValues.push(Math.round(10 + range * percentages[i]))
              }

              const uniqueTicks = Array.from(new Set(tickValues)).sort((a, b) => a - b)

              return (
                <div style={{ position: 'relative', height: '36px', paddingRight: '16px', marginBottom: '12px' }}>
                  {uniqueTicks.map(tickValue => {
                    const percentage = ((tickValue - 10) / range) * 100
                    return (
                      <div
                        key={`tick-${tickValue}`}
                        onClick={() => setWager(tickValue)}
                        style={{
                          position: 'absolute',
                          left: `${percentage}%`,
                          transform: 'translateX(-50%)',
                          textAlign: 'center',
                          cursor: 'pointer',
                          top: 0,
                        }}
                      >
                        <div
                          style={{
                            width: '1px',
                            height: '8px',
                            backgroundColor: wager === tickValue ? accent : '#d1d5db',
                            margin: '0 auto',
                          }}
                        />
                        <div
                          style={{
                            fontSize: '0.65rem',
                            fontWeight: wager === tickValue ? '700' : '500',
                            color: wager === tickValue ? accent : '#999',
                            whiteSpace: 'nowrap',
                            marginTop: '4px',
                          }}
                        >
                          ${tickValue}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )
            })()}

            {/* Floating wager value below controls */}
            <div style={{
              padding: '8px 12px',
              backgroundColor: accentLight,
              borderRadius: '8px',
              textAlign: 'center',
              marginBottom: '12px',
            }}>
              <div style={{
                fontSize: '0.65rem',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: '#999',
                marginBottom: '4px',
              }}>
                Selected Wager
              </div>
              <div style={{
                fontSize: '1.3rem',
                fontWeight: '900',
                color: accent,
              }}>
                ${wager.toFixed(2)}
              </div>
            </div>
          </div>

          {/* Possible Outcomes */}
          <div style={{ marginTop: '16px' }}>
              <div style={{ fontSize: '0.65rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#999', marginBottom: '6px' }}>Possible Outcomes</div>

              {/* Column Headers */}
              <div style={{ display: 'grid', gridTemplateColumns: '0.6fr 1fr 1fr 1fr', gap: '4px', marginBottom: '4px', paddingBottom: '4px', borderBottom: `1px solid #e5e7eb` }}>
                <div style={{ fontSize: '0.6rem', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Score</div>
                <div style={{ fontSize: '0.6rem', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'center' }}>Payout</div>
                <div style={{ fontSize: '0.6rem', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'right' }}>Banked Today</div>
                <div style={{ fontSize: '0.6rem', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'right' }}>Banked This Week</div>
              </div>

              {/* Outcomes */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '4px' }}>
                {[
                  { accuracy: 0, label: '0/4', scoreMultiplier: -1 },
                  { accuracy: 1, label: '1/4', scoreMultiplier: -0.5 },
                  { accuracy: 2, label: '2/4', scoreMultiplier: 0 },
                  { accuracy: 3, label: '3/4', scoreMultiplier: 0.5 },
                  { accuracy: 4, label: '4/4', scoreMultiplier: 1 },
                ].map(({ accuracy, label, scoreMultiplier }) => {
                  const dayMultiplier = isSundayGame ? DAY_MULTIPLIER.sunday : DAY_MULTIPLIER.weekday
                  const payout = wager * scoreMultiplier * dayMultiplier
                  const bankedToday = DAILY_CREDIT + payout
                  const newWeeklyBank = weeklyBank + bankedToday
                  const isPositive = payout >= 0

                  return (
                    <div
                      key={accuracy}
                      style={{
                        padding: '6px 8px',
                        backgroundColor: isPositive ? '#f0fdf4' : '#fef2f2',
                        borderRadius: '6px',
                        border: `1px solid ${isPositive ? '#dcfce7' : '#fee2e2'}`,
                        display: 'grid',
                        gridTemplateColumns: '0.6fr 1fr 1fr 1fr',
                        gap: '8px',
                        alignItems: 'center',
                      }}
                    >
                      <div style={{ fontSize: '0.75rem', fontWeight: '700', color: textPrimary }}>
                        {label}
                      </div>
                      <div style={{ fontSize: '0.7rem', fontWeight: '600', color: isPositive ? '#15803d' : '#dc2626', textAlign: 'center' }}>
                        {isPositive ? '+' : ''}{payout.toFixed(2)}
                      </div>
                      <div style={{ fontSize: '0.7rem', fontWeight: '600', color: bankedToday >= 0 ? textPrimary : '#dc2626', textAlign: 'right' }}>
                        ${bankedToday.toFixed(2)}
                      </div>
                      <div style={{ fontSize: '0.7rem', fontWeight: '600', color: newWeeklyBank >= 0 ? textPrimary : '#dc2626', textAlign: 'right' }}>
                        ${newWeeklyBank.toFixed(2)}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

        </div>

        {/* Start Game Button */}
        <button
          onClick={handleSubmit}
          style={isHovering ? primaryBtnHover : primaryBtn}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          Start Game
        </button>
      </div>
    </div>
  )
}

export default WagerScreen
